import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ManagerOrderReviewRow } from '../../../../Interfaces/ManagerOrderReviewRow';
import { ManagerOrderRow } from '../../../../Interfaces/MangerOrderRow';
import { apiAxios } from '../../../../utils/apiAxios';

// Define interfaces
interface ModalScanFile {
  path: string;
  name: string;
}

type ModalTab = "order-status" | "order-review" | "actions";

// Helper function to properly construct URLs
const constructFileUrl = (filePath: string): string => {
  const cleanPath = filePath?.trim();

  if (!cleanPath) return "";

  // File path is already a complete URL
  if (/^https?:\/\//i.test(cleanPath)) {
    return cleanPath;
  }

  // Same base used by the dashboard STL viewer
  const baseUrl = (
    process.env.REACT_APP_API_PATH_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/+$/, "");

  return `${baseUrl}/${cleanPath.replace(/^\/+/, "")}`;
};

const downloadFile = async (filePath: string, fileName: string) => {
  const fullUrl = constructFileUrl(filePath);

  try {
    const token = sessionStorage.getItem("token");

    const response = await apiAxios.get(fullUrl, {
      responseType: "blob", // ✅ IMPORTANT
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        AuthorizationData: token || "",
      },
    });

    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    toast.success(`Downloading ${fileName}`);
    return true;
  } catch (error: any) {
    console.warn("❌ Axios download failed:", error);

    // 🔁 Fallback (keep this — it's good)
    try {
      window.open(fullUrl, "_blank");
      toast.info(
        "File opened in new tab. Right-click and select 'Save As' to download."
      );
      return false;
    } catch {
      toast.error("Failed to download file");
      return false;
    }
  }
};



// Helper function to render design files
const renderDesignFiles = (review: ManagerOrderReviewRow): React.ReactNode => {
  const designerFileNames = review.designerFileNames || "";
  const designerFilePaths = review.designerFilePaths || "";
  
  
  if (!designerFileNames.trim() || !designerFilePaths.trim()) {
    return <span className="text-muted">No design files</span>;
  }

  const fileNames = designerFileNames.split(",").map(name => name.trim()).filter(Boolean);
  const filePaths = designerFilePaths.split(",").map(path => path.trim()).filter(Boolean);

  if (fileNames.length === 0) {
    return <span className="text-muted">No valid file names</span>;
  }

  return (
    <div className="design-files-list">
      {fileNames.map((fileName, index) => {
        const filePath = filePaths[index];
        if (!filePath) return null;

        return (
          <div key={index} className="d-flex align-items-center justify-content-between   px-2 ">
            <div className="d-flex align-items-center">
          
              <span className="text-truncate" style={{ maxWidth: "120px" }} title={fileName}>
                {fileName}
              </span>
            </div>
            <button
              className="btn btn-sm text-primary"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await downloadFile(filePath, fileName);
              }}
              title={`Download ${fileName}`}
            >
             <i className="bi bi-download"></i>
            </button>
          </div>
        );
      })}
    </div>
  );
};

// Props interfaces
interface OrderStatusContentProps {
  orderDetails: any;
}

interface OrderReviewContentProps {
  orderReview: ManagerOrderReviewRow[];
  scans: ModalScanFile[];
  onViewStl: (scan: ModalScanFile) => void;
}

interface ActionsContentProps {
  isDesignAcceptanceOverdue: boolean;
  isMillingAcceptanceOverdue: boolean;
  isDesignerRejected: boolean;
   orderDetails: any;
  isMillingRejected: boolean;
  selectedOrder: ManagerOrderRow | null;
  cancelReason: string;
  designers: any[];
  millings: any[];

 onReassignDesigner: (
    caseId: number,
    designerId: number
  ) => Promise<boolean> | void;

  onReassignMilling: (
    caseId: number,
    millingId: number
  ) => Promise<boolean> | void;
  onCancelOrder: (caseId: number, reason: string) => Promise<void> | void;
  onCancelReasonChange: (reason: string) => void;

    deliveryPartners: any[];

  onReassignDelivery: (
    caseId: number,
    deliveryPartnerId: number
  ) => Promise<boolean> | void;
  
}



// Component 1: OrderStatusContent
const OrderStatusContent: React.FC<OrderStatusContentProps> = ({ orderDetails }) => {
  const [openDelayIndex, setOpenDelayIndex] = useState<number | null>(null);
  const isDesignService =orderDetails?.isDesignService === true ||orderDetails?.isDesignService === "true";
  return (
    <div className="row g-0 p-3">
      <div className="col-md-6 lg-pe-4 border-end">
        <ul className="list-unstyled order-status-list delivery-type-section">
          <li className="time-event-section-delivery-type">
            <strong className="strongtext-fontweight Delivery-Type-text">Delivery Type</strong>
            <span>{orderDetails?.deliveryType || "--"}</span>
          </li>
          <li className="time-event-section-delivery-type mt-1">
            <strong className="strongtext-fontweight Delivery-Type-text">Payment Status</strong>
            <span>{orderDetails?.paymentStatus || "--"}</span>
          </li>
          <li className="time-event-section-delivery-type mt-1">
            <strong className="strongtext-fontweight Delivery-Type-text">Original ETA</strong>
            <span>
              {orderDetails?.originalETA
                ? new Date(orderDetails.originalETA).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "--"}
            </span>
          </li>
          <li className="time-event-section-delivery-type mt-1">
            <strong className="strongtext-fontweight Delivery-Type-text">Expected ETA</strong>
            <span>{orderDetails?.expectedETA
                ? new Date(orderDetails.expectedETA).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "--"}</span>
          </li>
        </ul>

   <div className="accordion mt-3" id="orderStatusAccordion">
  {["clinic", "design", "milling", "deliveryPartner"].map((section) => (
    <div key={section} className="accordion-item border rounded p-2 mt-3">
      <h2 className="accordion-header">
        <div
          className={`accordion-button ${section === "clinic" ? "" : "collapsed"}`}
          data-bs-toggle="collapse"
          data-bs-target={`#${section}Details`}
        >
          <strong className="strongtext-fontweight Delivery-Type-text">
            {section === "deliveryPartner"
              ? "Delivery Partner"
              : section.charAt(0).toUpperCase() + section.slice(1)}
          </strong>
        </div>
      </h2>

      <div
        id={`${section}Details`}
        className={`accordion-collapse collapse ${section === "clinic" ? "show" : ""}`}
        data-bs-parent="#orderStatusAccordion"
      >
        <div className="accordion-body">
          <div>
            {/* Name */}
            <div className="doctor-name-bottom">
              {orderDetails?.[section] || "--"}
            </div>

            {/* DESIGN → only name */}
            {section === "design" && ( <>
    <div className="doctor-name-bottom">
      {orderDetails?.designMobileNo || "--"}
    </div>
  </>)}

            {/* DELIVERY PARTNER → show all */}
            {section === "deliveryPartner" && (
              <>
                <div className="doctor-name-bottom">
                  {orderDetails?.deliveryPartnerEmail || "--"}
                </div>
                <div className="doctor-name-bottom">
                  {orderDetails?.deliveryPartnerMobileNo || "--"}
                </div>
                {/* <div className="doctor-name-bottom">
                  {orderDetails?.deliveryPartnerAddress || "--"}
                </div> */}
              </>
            )}

            {/* CLINIC & MILLING → mobile + address */}
            {section !== "design" && section !== "deliveryPartner" && (
              <>
                <div className="doctor-name-bottom">
                  {orderDetails?.[`${section}MobileNo`] || "--"}
                </div>
                <div className="doctor-name-bottom">
                  {orderDetails?.[`${section}Address`] || "--"}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ))}
</div>
      </div>

      <div className="col-md-6 table-sectionps-4">
        <ul className="list-unstyled order-status-timeline">
          <li className="time-event-sectionborder-bottom time-event-section pb-2 mb-2">
            <strong className="strongtext-fontweight">Time</strong>
            <strong className="strongtext-fontweight">Event</strong>
            <strong className="strongtext-fontweight">Status</strong>
          </li>
   {[
  {
    time: orderDetails?.createdTime,
    event: "Order Created",
    status: "Created",
  },

  {
  time: orderDetails?.paymentTime,
  event: (
    <div>
      <div>
        <strong>Payment Successful</strong>
      </div>

      <div className="small text-muted mt-1">
        <div>Original Amount : ₹{orderDetails?.originalAmount ?? "--"}</div>
        <div>Discount Amount : ₹{orderDetails?.discountAmount ?? "--"}</div>
        <div>Coupon : {orderDetails?.couponPercentage ?? "--"}% Off</div>
        <div>
          <strong>Paid Amount : ₹{orderDetails?.paidAmount ?? "--"}</strong>
        </div>
      </div>
    </div>
  ),
  status: "Paid",
},

  {
    time: orderDetails?.designerActualApprovalTime,
    event:
      orderDetails?.designerApprovalStatus !== "Pending"
        ? `Scan Approved by ${orderDetails.design} (Designer)`
        : "Scan not approved yet",
    status: orderDetails?.designerApprovalStatus,
  },

  // ✅ DESIGNER DELAY
  ...(orderDetails?.designerDelayedOn
    ? [
        {
          time: orderDetails?.designerDelayedOn,
          event: `Delay by ${orderDetails.design} (Designer)`,
          status: "Delayed",
          type: "designer",
        },
      ]
    : []),

...(!isDesignService
  ? [
      {
        time: orderDetails?.millingActualApprovalTime,
        event:
          orderDetails?.millingApprovalStatus !== "Pending"
            ? `${orderDetails?.milling || "--"} accepted order`
            : "Awaiting milling acceptance",
        status:
          orderDetails?.millingApprovalStatus || "Pending",
      },

      ...(orderDetails?.millingDelayedOn
        ? [
            {
              time: orderDetails?.millingDelayedOn,
              event: `Delay by Milling (${
                orderDetails?.milling || "--"
              })`,
              status: "Delayed",
              type: "milling",
            },
          ]
        : []),
    ]
  : []),

  ...(orderDetails?.millingDelayedOn
    ? [
        {
          time: orderDetails?.millingDelayedOn,
          event: `Delay by Milling (${orderDetails.milling})`,
          status: "Delayed",
          type: "milling",
        },
      ]
    : []),

  {
    time: "Current",
    event: orderDetails?.currentStatus || "--",
    status: orderDetails?.delayed,
  },
]
  // ...same array as before...
.map((item: any, index) => (
  <React.Fragment key={index}>

  <li
  className="time-event-section"
  style={{
    display: "grid",
    gridTemplateColumns: "100px 1fr 102px",
    alignItems: "start",
    columnGap: "4px",
  }}
>

  <strong
    className="strongtext-fontweight Delivery-Type-text"
    style={{ justifySelf: "start" }}
  >
    {item.time || "--"}
  </strong>

  {/* 👇 span ki jagah ab div hai — block-level JSX (Payment Successful) ko theek se render karega */}
  <div
    style={{
      cursor: item.status === "Delayed" ? "pointer" : "default",
      justifySelf: "start",
      textAlign: "left",
      width: "100%",
    }}
    onClick={() => {
      if (item.status === "Delayed") {
        setOpenDelayIndex(
          openDelayIndex === index ? null : index
        );
      }
    }}
  >
    {item.event}
  </div>

  <span
    className={`status-part ${
      item.status === "Delayed"
        ? "text-danger fw-bold"
        : ""
    }`}
    style={{ justifySelf: "start" }}
  >
    {item.status || "-"}{" "}

    {item.status === "Delayed"
      ? openDelayIndex === index
        ? "▲"
        : "▼"
      : ""}
  </span>

</li>

    {/* ---- REASON: ab bilkul alag <li> hai, koi flex/grid constraint nahi lagegi ---- */}
    {item.status === "Delayed" &&
      openDelayIndex === index && (
        <li
          className="ps-2 mt-1 mb-2 text-muted small"
          style={{
            display: "block",
            width: "100%",
            maxWidth: "100%",
            listStyle: "none",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >

          {item.type === "designer" && (
            <>
              Reason:{" "}
              {orderDetails?.designerDelayReason || "--"}
            </>
          )}

          {item.type === "milling" && (
            <>
              Reason:{" "}
              {orderDetails?.millingDelayReason || "--"}
            </>
          )}

        </li>
      )}

  </React.Fragment>
))}

        </ul>
      </div>
    </div>
  );
};
// Component 2: OrderReviewContent
const OrderReviewContent: React.FC<OrderReviewContentProps> = ({ 
  orderReview, 
  scans, 
  onViewStl 
}) => {
  const handleScanDownload = async (scan: ModalScanFile) => {
    await downloadFile(scan.path, scan.name);
  };

  // Debug scans data
  // React.useEffect(() => {
  //   if (scans.length > 0) {
  //   }
  // }, [scans]);

  return (
    <div className="row g-0 p-3">
          <div className="col-lg-9  col-md-12 col-12">
        <table className="table table-sm table-content-insidethetable table-borderless">
          <thead>
            <tr>
              <th>Tooth</th>
              <th>Material</th>
              <th>Shade</th>
              <th>Design</th>
            </tr>
          </thead>
          <tbody>
            {orderReview.map((review, index) => (
              <tr key={index}>
                <td>{review.tooth || "--"}</td>
                <td>{review.material || "--"}</td>
                <td>{review.shade || "--"}</td>
                <td className="scrollable-td text-truncate" style={{ minWidth: "200px" }}>
                  <div className="imageloaded-stlfile-padding-right-file">
                    {renderDesignFiles(review)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

     <div className="col-lg-3 col-md-12 col-12 border-start scans-section-padding0">
        <strong className="scans-fontweight500 mb-2 mt-3 d-block">Scans (By Dentist)</strong>
        {scans.length === 0 ? (
          <p className="text-muted">No scans available</p>
        ) : (
          scans.map((scan, index) => (
            <div key={index} className="d-flex justify-content-between align-items-center mt-2 px-2 py-1 border rounded">
              <span className="text-truncate" style={{ maxWidth: "120px" }} title={scan.name}>
             
                {scan.name}
              </span>
              <div className="d-flex gap-2">
                <i
                  className="bi bi-eye"
                  style={{ cursor: "pointer", fontSize: "1rem" }}
                  title="View STL"
                  onClick={() => {
                    onViewStl(scan);
                  }}
                />
                <i
                  className="bi bi-download"
                  style={{ cursor: "pointer", fontSize: "1rem", color: "#0d6efd" }}
                  title="Download STL"
             

                 onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await downloadFile(scan.path, scan.name);
              }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};



// Component 3: ActionsContent
const ActionsContent: React.FC<ActionsContentProps> = ({
  isDesignAcceptanceOverdue,
  isMillingAcceptanceOverdue,
  isDesignerRejected,
  isMillingRejected,
  selectedOrder,
  cancelReason,
  designers,
  millings,
    orderDetails,
  onReassignDesigner,
  onReassignMilling,
  deliveryPartners,
  onCancelOrder,
  onCancelReasonChange,
   onReassignDelivery,
}) => {
  React.useEffect(() => {
  console.warn("ActionsContent designers:", designers);
}, [designers]);
React.useEffect(() => {
  console.warn("ActionsContent deliveryPartners:", deliveryPartners);
}, [deliveryPartners]);


  const [showCancel, setShowCancel] = useState(false);
   const [showRequestPopup, setShowRequestPopup] = useState(false);
const [showDeliveryDropdown, setShowDeliveryDropdown] =
  useState(false);
  const [showDesignerDropdown, setShowDesignerDropdown] = useState(false);
const [showMillingDropdown, setShowMillingDropdown] = useState(false);

const [selectedDesigner, setSelectedDesigner] = useState("");
const [selectedMilling, setSelectedMilling] = useState("");
const isDesignService = selectedOrder?.type === "Design Service";
console.log("Selected Order:", selectedOrder);
console.log("Selected Order State:", selectedOrder?.state);
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] =
  useState("");
  const canReassignDelivery =
  selectedOrder?.state === "IN_MILLING" ||
  selectedOrder?.state === "READY_FOR_DELIVERY";
const canCancel =
  selectedOrder?.status !== "Cancelled" &&
  selectedOrder?.state !== "READY_FOR_DELIVERY" &&
  selectedOrder?.state !== "OUT_FOR_DELIVERY" &&
  selectedOrder?.state !== "IN_MILLING" &&
  !(isDesignService && selectedOrder?.state === "IN_DESIGN");
  const hideMillingReassignStatuses = [
  "Done",
  "OUT_FOR_DELIVERY",
];

const shouldHideMillingReassign =
  hideMillingReassignStatuses.some(
    (status) =>
      selectedOrder?.MillingStatus?.toLowerCase() === status.toLowerCase()
  );
  return (
    <div className="p-3">
      {isDesignerRejected && (
        <div className="alert alert-warning mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Designer has rejected this order
        </div>
      )}
      
      {isMillingRejected && (
        <div className="alert alert-warning mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Milling hub has rejected this order
        </div>
      )}

          {/* ================= REASSIGN DESIGNER ================= */}

{selectedOrder?.DesignerStatus !== "Done" && (
  <>
    <ul className="report-delay-list-action">
      <li
        className="fw-semibold cursor-pointer"
        onClick={() =>
          setShowDesignerDropdown(!showDesignerDropdown)
        }
      >
        Reassign Designer
      </li>
    </ul>

    {showDesignerDropdown && (
      <div className="mb-3 row">
        <div className="col-md-6 mx-auto my-3">
          <select
            className="form-select"
            value={selectedDesigner}
            onChange={(e) =>
              setSelectedDesigner(e.target.value)
            }
          >
            <option value="">Select Designer</option>

            {designers?.map((designer: any) => (
              <option
                key={designer.userID}
                value={designer.userID}
              >
                {designer.name}
              </option>
            ))}
          </select>

          <div className="d-flex justify-content-center mt-2">
            <button
              className="btn btn-primary btn-sm mt-3"
              disabled={!selectedDesigner}
              onClick={() => {
                if (!selectedOrder) return;

                onReassignDesigner(
                  selectedOrder.caseID,
                  Number(selectedDesigner)
                );
              }}
            >
              Assign Designer
            </button>
          </div>
        </div>
      </div>
    )}
  </>
)}
 {/* ================= REASSIGN MILLING ================= */}

{!isDesignService && !shouldHideMillingReassign &&  (
    <>
      <ul className="report-delay-list-action">
        <li
          className="fw-semibold cursor-pointer"
          onClick={() =>
            setShowMillingDropdown(!showMillingDropdown)
          }
        >
          Reassign Milling Hub
        </li>
      </ul>

      {showMillingDropdown && (
        <div className="mb-3 row">
          <div className="col-md-6 mx-auto my-3">
            <select
              className="form-select"
              value={selectedMilling}
              onChange={(e) =>
                setSelectedMilling(e.target.value)
              }
            >
              <option value="">Select Milling Hub</option>

              {millings?.map((milling: any) => (
                <option
                  key={milling.userID}
                  value={milling.userID}
                >
                  {milling.name}
                </option>
              ))}
            </select>

            <div className="d-flex justify-content-center mt-3">
              <button
                className="btn btn-primary btn-sm mt-2"
                disabled={!selectedMilling}
                onClick={() => {
                  if (!selectedOrder) return;

                  onReassignMilling(
                    selectedOrder.caseID,
                    Number(selectedMilling)
                  );
                }}
              >
                Assign Milling
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )}

  {/* ================= REASSIGN DELIVERY ================= */}
{!isDesignService && canReassignDelivery && (
  <>
    <ul className="report-delay-list-action">
      <li
        className="fw-semibold cursor-pointer"
        onClick={() =>
          setShowDeliveryDropdown(!showDeliveryDropdown)
        }
      >
        Reassign Delivery Partner 
      </li>
    </ul>

    {showDeliveryDropdown && (
      <div className="mb-3 row">
        <div className="col-md-6 mx-auto my-3">
          <select
            className="form-select"
            value={selectedDeliveryPartner}
            onChange={(e) =>
              setSelectedDeliveryPartner(e.target.value)
            }
          >
            <option value="">
              Select Delivery Partner
            </option>

            {deliveryPartners?.map((partner: any) => (
              <option
                key={partner.userID}
                value={partner.userID}
              >
                {partner.name}
              </option>
            ))}
          </select>

          <div className="d-flex justify-content-center mt-3">
            <button
              className="btn btn-primary btn-sm mt-2"
              disabled={!selectedDeliveryPartner}
              onClick={() => {
                if (!selectedOrder) return;

                onReassignDelivery(
                  selectedOrder.caseID,
                  Number(selectedDeliveryPartner)
                );
              }}
            >
              Assign Delivery
            </button>
          </div>
        </div>
      </div>
    )}
  </>
)}
{canCancel && (
  <ul className="report-delay-list-action">
    <li
      className="fw-semibold cursor-pointer"
      onClick={() => {
        const status = orderDetails?.cancellationRequestStatus;

        if (status === null || status === "REJECTED") {
          setShowCancel(!showCancel);
        } else {
          setShowRequestPopup(true);
        }
      }}
    >
      Cancel Order
    </li>
  </ul>
)}



      {showCancel && (
        <div className="mt-1">
 
          <div className="mb-3">
            <textarea
              className="form-control"
              placeholder="Enter cancellation reason"
              rows={3}
              value={cancelReason}
              onChange={(e) => onCancelReasonChange(e.target.value)}
            />
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              disabled={!cancelReason.trim()}
              onClick={() => {
                if (!selectedOrder) return;
                onCancelOrder(selectedOrder.caseID, cancelReason);
                setShowCancel(false);
              }}
            >
   
               Cancel Order
            </button>
        
          </div>
        </div>
      )}
{showRequestPopup && (
  <div
    className="custom-popup-overlay"
    onClick={() => setShowRequestPopup(false)}
  >
    <div
      className="custom-popup cancellation-popup"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close Button */}
      <button
        type="button"
        className="popup-close"
        onClick={() => setShowRequestPopup(false)}
        aria-label="Close"
      >
        ×
      </button>

      {/* Icon */}
      <div className="popup-icon">
        <span>!</span>
      </div>

      {/* Content */}
      <div className="popup-content">
        <h5>Cancellation Request Pending</h5>

        <p>
          A cancellation request has already been submitted for this order.
        </p>

      </div>

      {/* Action */}
      <div className="popup-footer">
        <button
          type="button"
          className="popup-ok-btn"
          onClick={() => setShowRequestPopup(false)}
        >
          Okay
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export { OrderStatusContent, OrderReviewContent, ActionsContent };
export type { ModalTab, ModalScanFile };